import { useFormikContext } from 'formik';
import { useCallback } from 'react';

import { type PartFileUnits } from '@frontend/api';
import { VStack } from '@frontend/legacy-components';

import { useReqsReviewConfigContext } from '../ReqsReviewConfigContext';
import {
  type GroupWithId,
  type QueryWithId,
  type SignalWithId,
} from '../utils/queryBuilding';
import { FilterQueryWrapped } from './FilterQueryWrapped';
import { type FormStateData } from './RulesDetailModal';

type FilterQueryListProps = {
  group: GroupWithId;
  groupIndex: number;
  signalIndex: number;
  signal: SignalWithId;
  isReadOnly: boolean;
  unit: PartFileUnits;
};

export const FilterQueryList = ({
  group,
  groupIndex,
  signalIndex,
  signal,
  isReadOnly,
  unit,
}: FilterQueryListProps) => {
  const { values, setFieldValue } = useFormikContext<FormStateData>();
  const signals = values.signals;

  const { configMap } = useReqsReviewConfigContext();

  const handleQueryValueChange = useCallback(
    (queryIndex: number, value: QueryWithId) => {
      const updatedSignal = { ...signal };
      const query = updatedSignal.groups[groupIndex].queries[queryIndex];
      updatedSignal.groups[groupIndex].queries[queryIndex] = {
        ...query,
        ...value,
      };

      const newSignals = [...signals];
      newSignals[signalIndex] = updatedSignal;
      setFieldValue('signals', newSignals);
    },
    [signals, setFieldValue, signal, signalIndex, groupIndex],
  );

  const handleClearQuery = useCallback(
    (queryIndex: number) => {
      const updatedGroup = { ...group };
      updatedGroup.queries.splice(queryIndex, 1);

      // Clear the entire group if no queries remain and there's no count query
      if (updatedGroup.queries.length === 0 && !updatedGroup.countQuery) {
        const newSignals = [...signals];
        const updatedSignal = { ...newSignals[signalIndex] };
        updatedSignal.groups.splice(groupIndex, 1);
        if (updatedSignal.groups.length === 0) {
          newSignals.splice(signalIndex, 1);
        } else {
          newSignals[signalIndex] = updatedSignal;
        }
        setFieldValue('signals', newSignals);
      } else {
        const updatedSignal = { ...signal };
        updatedSignal.groups[groupIndex] = updatedGroup;
        const newSignals = [...signals];
        newSignals[signalIndex] = updatedSignal;
        setFieldValue('signals', newSignals);
      }
    },
    [signals, setFieldValue, group, signal, signalIndex, groupIndex],
  );

  if (!configMap) return null;

  return (
    <VStack spacing={1} align="stretch" className="filter-query-list">
      {group.queries.map((query, queryIndex) => {
        // Show remove button if there are multiple queries in this group
        // or if there are other filters (like count query) in the group
        const hasMultipleQueries = group.queries.length > 1;
        const hasOtherFilters = group.countQuery !== null;
        const hideRemoveButton = !hasMultipleQueries && !hasOtherFilters;

        return (
          <FilterQueryWrapped
            key={query.id}
            query={query}
            group={group}
            queryIndex={queryIndex}
            signalIndex={signalIndex}
            groupIndex={groupIndex}
            isReadOnly={isReadOnly}
            onClearQuery={handleClearQuery}
            onQueryValueChange={handleQueryValueChange}
            unit={unit}
            hideRemoveButton={hideRemoveButton}
          />
        );
      })}
    </VStack>
  );
};
